<template>
  <BaseCard type="filled" class="min-w-0 grow flex-col p-4">
    <h3 class="text-title-small mb-4">Verlauf</h3>
    <div class="relative h-36 grow">
      <div class="absolute top-0 left-0 z-0 flex h-full w-full flex-col justify-between pb-6">
        <div
          v-for="_ in scale.items"
          class="border-t-surface-container-highest w-full border-t"
        ></div>
      </div>
      <div class="relative z-10 flex h-full flex-row justify-end">
        <div
          v-for="(survey, index) in sortedSurveys"
          :key="survey.id"
          class="group flex h-full w-10 flex-col items-center"
          :class="[props.selectable && 'cursor-pointer']"
          @click="selectSurvey(survey.id)"
        >
          <div class="flex grow flex-col justify-end">
            <div
              class="ease-fast-spatial relative flex min-h-2 w-7 flex-col rounded-full p-1 transition-[height] duration-300"
              :class="[
                selectedSurveyId === survey.id && 'bg-primary text-primary-container',
                selectedSurveyId !== survey.id && 'bg-primary-container text-on-primary-container',
              ]"
              :style="{
                height: getBarHeight(survey.average),
                transitionDelay: index * (300 / sortedSurveys.length) + 'ms',
              }"
            >
              <div
                v-if="survey.average != null && survey.average < 0.8 && survey.average > 0"
                class="absolute top-1.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full"
                :class="[
                  selectedSurveyId === survey.id && 'bg-primary-container',
                  selectedSurveyId !== survey.id && 'bg-primary',
                ]"
              ></div>
              <Transition
                v-if="survey.average != null && survey.average >= 0.8"
                appear
                appear-active-class="transition-transform duration-400 ease-fast-spatial"
                appear-from-class="-rotate-360 scale-20"
                appear-to-class="rotate-0 scale-100"
              >
                <BaseShape
                  shape="burst"
                  :class="[
                    selectedSurveyId === survey.id && 'text-primary-container',
                    selectedSurveyId !== survey.id && 'text-on-primary-container',
                  ]"
                  class="text-on-primary-container absolute top-1 left-1/2 h-4.5 w-4.5 -translate-x-1/2"
                  :style="{ transitionDelay: index * (300 / sortedSurveys.length) + 100 + 'ms' }"
                />
              </Transition>
            </div>
          </div>
          <div class="h-6">
            <span class="text-label-small text-on-surface-variant text-center">
              {{
                useDisplayDate(survey.date, {
                  dayName: 'none',
                  monthName: 'number',
                  year: 'none',
                })
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
  import BaseCard from '../../../core/components/base/card/BaseCard.vue'
  import { useDisplayDate } from '../../composables/use-display-date'
  import BaseShape from '../../../core/components/base/shapes/BaseShape.vue'
  import type { JournalAnalysis } from './journal-analysis'
  import type { Scale } from '@/core/models/scale'
  import { computed, ref, onMounted } from 'vue'

  interface Props {
    surveys: ReadonlyArray<Omit<JournalAnalysis['surveys'][number], 'participantIds'>>
    scale: Scale
    selectable?: boolean
  }

  const props = defineProps<Props>()

  const selectedSurveyId = defineModel<string | undefined>('selectedSurveyId')

  const sortedSurveys = computed(() => {
    return [...props.surveys].reverse()
  })

  const hasMounted = ref(false)

  const getBarHeight = (value: number | null) => {
    if (!hasMounted.value) {
      return '0%'
    }

    return `${(value ?? 0) * 100}%`
  }

  onMounted(() => {
    requestAnimationFrame(() => {
      hasMounted.value = true
    })
  })

  const selectSurvey = (surveyId: string) => {
    if (props.selectable === false) {
      return
    }
    selectedSurveyId.value = surveyId
  }
</script>
